package top.wcpe.allincore.ui.runtime.style

import kotlin.jvm.JvmField
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.min

/**
 * 四角圆角值对象：TL / TR / BR / BL 顺序与 shader 的 `vec4 CornerRadii` uniform 对齐。
 *
 * 不可变；用 [toBuilder] 派生新实例。提供 CSS 简写解析（1 / 2 / 4 元素列表）
 * 与按高度钳取的工具方法。
 *
 * 四角访问器以 [tl] / [tr] / [br] / [bl] 暴露，经 `@get:JvmName` 保留无 `get`
 * 前缀的 fluent 访问器名（`tl()` 等），与既有 Java 调用方二进制兼容。
 */
@Suppress("MagicNumber")
class CornerRadii private constructor(
    @get:JvmName("tl") val tl: Float,
    @get:JvmName("tr") val tr: Float,
    @get:JvmName("br") val br: Float,
    @get:JvmName("bl") val bl: Float,
) {

    /** 顺序 [TL, TR, BR, BL]，shader uniform 直传。 */
    fun toArray(): FloatArray = floatArrayOf(tl, tr, br, bl)

    fun isZero(): Boolean = tl == 0f && tr == 0f && br == 0f && bl == 0f

    /** 四角是否相等。 */
    fun isUniform(): Boolean = tl == tr && tr == br && br == bl

    /** 取四角最大值，用于「任一角非零」的判定。 */
    fun maxRadius(): Float {
        var m = tl
        if (tr > m) m = tr
        if (br > m) m = br
        if (bl > m) m = bl
        return m
    }

    /**
     * 按 maxRadius（通常为 height/2）逐角钳取，避免任意角超过半短边导致 SDF 退化。
     * maxRadius ≤ 0 时返回 [ZERO]。
     */
    fun clampedTo(maxRadius: Float): CornerRadii {
        if (maxRadius <= 0f) return ZERO
        val allWithin = tl <= maxRadius && tr <= maxRadius && br <= maxRadius && bl <= maxRadius
        if (allWithin) {
            return this
        }
        return of(
            min(tl, maxRadius),
            min(tr, maxRadius),
            min(br, maxRadius),
            min(bl, maxRadius),
        )
    }

    /** 派生构建器，预填当前四角值；与 Lombok `toBuilder = true` 行为一致。 */
    fun toBuilder(): Builder = Builder().tl(tl).tr(tr).br(br).bl(bl)

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is CornerRadii) return false
        return tl == other.tl && tr == other.tr && br == other.br && bl == other.bl
    }

    override fun hashCode(): Int {
        var result = tl.hashCode()
        result = 31 * result + tr.hashCode()
        result = 31 * result + br.hashCode()
        result = 31 * result + bl.hashCode()
        return result
    }

    /**
     * 四角圆角构建器：复刻 Lombok `@Builder(builderClassName = "Builder", toBuilder = true)`
     * 的链式 ABI（`CornerRadii.builder().tl(x)...build()`）。各角默认 0f，对应原 `@Builder.Default`。
     */
    class Builder internal constructor() {
        private var tl = 0f
        private var tr = 0f
        private var br = 0f
        private var bl = 0f

        fun tl(value: Float): Builder = apply { tl = value }
        fun tr(value: Float): Builder = apply { tr = value }
        fun br(value: Float): Builder = apply { br = value }
        fun bl(value: Float): Builder = apply { bl = value }

        fun build(): CornerRadii = CornerRadii(tl, tr, br, bl)
    }

    companion object {
        /** 四角全 0 的共享实例。 */
        @JvmField
        val ZERO: CornerRadii = CornerRadii(0f, 0f, 0f, 0f)

        /** 四角等值快捷构造；r ≤ 0 时返回 [ZERO] 共享实例。 */
        @JvmStatic
        fun uniform(r: Float): CornerRadii {
            if (r <= 0f) return ZERO
            return CornerRadii(r, r, r, r)
        }

        /** 显式四角构造。参数顺序 TL / TR / BR / BL。 */
        @JvmStatic
        fun of(tl: Float, tr: Float, br: Float, bl: Float): CornerRadii {
            val allZero = tl == 0f && tr == 0f && br == 0f && bl == 0f
            if (allZero) return ZERO
            return CornerRadii(tl, tr, br, bl)
        }

        /**
         * CSS 简写解析：
         * - 1 元素：四角等值
         * - 2 元素：[TL+BR, TR+BL]（CSS 简写：上左下右 / 上右下左）
         * - 4 元素：[TL, TR, BR, BL]（顺时针自左上）
         *
         * 其它长度返回 [ZERO]。
         */
        @JvmStatic
        fun parseCss(values: List<Number>?): CornerRadii {
            if (values.isNullOrEmpty()) return ZERO
            return when (values.size) {
                1 -> uniform(values[0].toFloat())
                2 -> {
                    val tlbr = values[0].toFloat()
                    val trbl = values[1].toFloat()
                    of(tlbr, trbl, tlbr, trbl)
                }
                4 -> of(
                    values[0].toFloat(),
                    values[1].toFloat(),
                    values[2].toFloat(),
                    values[3].toFloat(),
                )
                else -> ZERO
            }
        }

        /** 构建器入口，复刻 Lombok 生成的 `CornerRadii.builder()`。 */
        @JvmStatic
        fun builder(): Builder = Builder()
    }
}
